/**
 * Globally-mounted SyncDataDialog host (Slice 10b).
 *
 * Any surface that wants to open the dialog calls
 * `useSyncDataDialogStore.getState().openSyncDataDialog(folderId)`. The
 * host resolves the full `MountedFolder` from the script-browser store
 * (active workspace falling back to default) so the dialog always sees
 * the freshest `linkedDbs` and `activeLinkLabel`.
 */

import { lazy, Suspense, useMemo } from 'react';
import type { SyncDataDialogLink } from './SyncDataDialog';
import { useSyncDataDialogStore } from '../store/syncDataDialogStore';
import { useScriptBrowserStore } from '../../../script-browser/store/scriptBrowserStore';
import type { MountedFolder } from '../../../script-browser/types/scriptBrowserTypes';

// Rendered only while open (see the `dialogState.open` guards below), so the
// picker stays out of the eager boot chunk even though the host is mounted at
// the App root.
const SyncDataDialog = lazy(() =>
  import('./SyncDataDialog').then((m) => ({ default: m.SyncDataDialog })),
);

function findFolderById(
  defaultFolders: ReadonlyArray<MountedFolder>,
  workspaces: ReadonlyArray<{ id: string; mountedFolders: MountedFolder[] }>,
  activeWorkspaceId: string | null,
  folderId: string,
): MountedFolder | undefined {
  if (activeWorkspaceId) {
    const ws = workspaces.find((w) => w.id === activeWorkspaceId);
    const hit = ws?.mountedFolders.find((f) => f.id === folderId);
    if (hit) return hit;
  }
  return defaultFolders.find((f) => f.id === folderId);
}

export function GlobalSyncDataDialog() {
  const dialogState = useSyncDataDialogStore((s) => s.state);
  const closeSyncDataDialog = useSyncDataDialogStore(
    (s) => s.closeSyncDataDialog,
  );
  const openSyncDataDialog = useSyncDataDialogStore(
    (s) => s.openSyncDataDialog,
  );
  const defaultFolders = useScriptBrowserStore(
    (s) => s.defaultWorkspaceState.mountedFolders,
  );
  const workspaces = useScriptBrowserStore((s) => s.workspaces);
  const activeWorkspaceId = useScriptBrowserStore((s) => s.activeWorkspaceId);

  const folder = useMemo<MountedFolder | undefined>(() => {
    if (!dialogState.open) return undefined;
    return findFolderById(
      defaultFolders,
      workspaces,
      activeWorkspaceId,
      dialogState.folderId,
    );
  }, [dialogState, defaultFolders, workspaces, activeWorkspaceId]);

  if (!dialogState.open) return null;
  // If the folder was removed while the dialog was queued, swallow the
  // open call rather than rendering with stale identity.
  if (!folder || folder.kind !== 'blueprint' || !folder.blueprintMetadata) {
    return null;
  }

  const linkedDbs: ReadonlyArray<SyncDataDialogLink> =
    folder.blueprintMetadata.linkedDbs.map((l) => ({
      label: l.label,
      connectionId: l.connectionId,
      connectionDisplayName: l.connectionDisplayName,
      database: l.database,
      engine: l.engine,
    }));

  return (
    <Suspense fallback={null}>
      <SyncDataDialog
        open={dialogState.open}
        onOpenChange={(next) => {
          if (!next) {
            closeSyncDataDialog();
          } else {
            // When the retry action re-opens the dialog after a connection-lost
            // close, it calls onOpenChange(true). Re-open by passing the same
            // folderId + opts back to the store (args already carry the full
            // context from the original open call).
            openSyncDataDialog(dialogState.folderId, {
              tabId: dialogState.tabId,
              activeLinkConnectionId: dialogState.activeLinkConnectionId,
            });
          }
        }}
        folderPath={folder.directoryPath}
        linkedDbs={linkedDbs}
        activeLinkLabel={folder.blueprintMetadata.activeLinkLabel}
        tabId={dialogState.tabId}
        activeLinkConnectionId={dialogState.activeLinkConnectionId}
      />
    </Suspense>
  );
}
